import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchUserInteractions } from 'actions/RecommendationActions'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCurrentUserId, selectRecDomainUserId } from 'selectors/accountSelectors'
import { canUseConsentPurposeOnClient } from '../../consent/waitingForConsent'

type Props = {
  interactionType: App.UserInteractionType
  sinceDaysAgo?: number
  disabled?: boolean
}

/** Fetch history, reusing completed Redux results. */
export function useUserInteractions({ interactionType, sinceDaysAgo, disabled = false }: Props): [
  interactions: Array<App.OfferInteraction | App.SearchInteraction>,
  fetching: boolean,
] {
  const dispatch = useAppDispatch()
  const interactionState = useAppSelector((state) => state.recommendations.userInteractions[interactionType])
  const hasUserId = useAppSelector(state => !!(getCurrentUserId(state) || selectRecDomainUserId(state)))
  const canFetch = !disabled && hasUserId && canUseConsentPurposeOnClient('personalisation')
  const fetching = canFetch && (!interactionState || interactionState.state === 'loading')
  const finalUserInteractions = useMemo(() => {
    if (disabled) {
      return EmptyArray
    }

    const userInteractions = interactionState?.interactions ?? []

    let finalUserInteractions = userInteractions
    if (sinceDaysAgo) {
      const sinceDateMs = Date.now() - sinceDaysAgo * 24 * 60 * 60 * 1000
      finalUserInteractions = userInteractions.filter((interaction) => {
        const interactionDateMs = interaction.interaction_time * 1000
        return interactionDateMs >= sinceDateMs
      })
    }

    if (!finalUserInteractions.length) return EmptyArray

    return finalUserInteractions
  }, [disabled, interactionState?.interactions, sinceDaysAgo])

  useEffect(() => {
    if (canFetch) {
      dispatch(fetchUserInteractions(interactionType, sinceDaysAgo))
    }
  }, [dispatch, interactionType, sinceDaysAgo, canFetch])

  return [finalUserInteractions, fetching]
}
